import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ConsolidationUrlParams,
  parseConsolidationUrlParams,
} from "@/lib/consolidationMetadata";
import { TIMELINE_AT_PARAM } from "@/lib/timelineUniverseParams";

export type ConsolidationViewerParamsUpdate = {
  changeLaw?: string | null;
  asOf?: string | null;
  timelineAt?: number | null;
};

const applyParamUpdate = (
  params: URLSearchParams,
  key: string,
  value: string | null | undefined
) => {
  if (value === null || value === undefined || value.trim() === "") {
    params.delete(key);
    return;
  }
  params.set(key, value.trim());
};

/**
 * Read/write consolidation viewer URL params without full page reload.
 */
export const useConsolidationViewerParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const urlParams = useMemo(
    () => parseConsolidationUrlParams(searchParams),
    [searchParams]
  );

  const updateParams = useCallback(
    (update: ConsolidationViewerParamsUpdate) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          if ("changeLaw" in update) {
            applyParamUpdate(next, "change_law", update.changeLaw);
          }
          if ("asOf" in update) {
            applyParamUpdate(next, "as_of", update.asOf);
          }
          if ("timelineAt" in update) {
            if (update.timelineAt == null) {
              next.delete(TIMELINE_AT_PARAM);
            } else {
              next.set(TIMELINE_AT_PARAM, String(update.timelineAt));
            }
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setAsOf = useCallback(
    (asOf: string | null) =>
      updateParams({
        asOf,
        changeLaw: null,
      }),
    [updateParams]
  );

  const setChangeLaw = useCallback(
    (changeLaw: string, asOf?: string | null, timelineAt?: number | null) => {
      const update: ConsolidationViewerParamsUpdate = {
        changeLaw,
        asOf: asOf ?? null,
      };
      if (timelineAt !== undefined) {
        update.timelineAt = timelineAt;
      }
      updateParams(update);
    },
    [updateParams]
  );

  return {
    urlParams,
    updateParams,
    setAsOf,
    setChangeLaw,
  };
};

export type { ConsolidationUrlParams };
